import type { UserSubscription } from 'types';

type RawSub = UserSubscription & Record<string, unknown>;

const str = (...vals: unknown[]): string | undefined => {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
};

const num = (...vals: unknown[]): number | undefined => {
  for (const v of vals) {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string' && v.trim() && !Number.isNaN(Number(v)))
      return Number(v);
  }
  return undefined;
};

/** Card or wallet summary for display, e.g. "Visa ····4242", when API provides brand + last4. */
export function getPaymentMethodSummary(
  sub: UserSubscription | null | undefined,
): string | null {
  if (!sub) return null;
  const r = sub as RawSub;
  const brand = str(r.paymentMethodBrand, r.payment_method_brand);
  const last4 = str(r.paymentMethodLast4, r.payment_method_last4);
  const brandNice = brand
    ? `${brand.charAt(0).toUpperCase()}${brand.slice(1).toLowerCase()}`
    : '';
  if (brandNice && last4) return `${brandNice} ····${last4}`;
  if (last4) return `····${last4}`;
  if (brandNice) return brandNice;
  return null;
}

/** Stripe Customer Portal or equivalent; optional env fallback for ops. */
export function getBillingPortalUrl(
  sub: UserSubscription | null | undefined,
): string | undefined {
  if (!sub) return undefined;
  const r = sub as RawSub;
  const u = str(r.billingPortalUrl, r.billing_portal_url);
  if (u) return u;
  const env = process.env.REACT_APP_BILLING_PORTAL_URL;
  return typeof env === 'string' && env.trim() ? env.trim() : undefined;
}

export function getCardExpiryParts(
  sub: UserSubscription | null | undefined,
): { month: number; year: number } | null {
  if (!sub) return null;
  const r = sub as RawSub;
  const m = num(r.paymentMethodExpMonth, r.payment_method_exp_month);
  const y = num(r.paymentMethodExpYear, r.payment_method_exp_year);
  if (m != null && y != null && m >= 1 && m <= 12) return { month: m, year: y };
  return null;
}

export function getLastInvoicePdfUrl(
  sub: UserSubscription | null | undefined,
): string | undefined {
  if (!sub) return undefined;
  const r = sub as RawSub;
  return str(r.lastInvoicePdfUrl, r.last_invoice_pdf_url);
}

export function getLastInvoiceMeta(sub: UserSubscription | null | undefined): {
  amount: number;
  currency: string;
  paidAt: string;
} | null {
  if (!sub) return null;
  const r = sub as RawSub;
  const amount = num(r.lastInvoiceAmount, r.last_invoice_amount);
  const paidAt = str(r.lastInvoicePaidAt, r.last_invoice_paid_at);
  if (amount == null || !paidAt) return null;
  const currency = str(r.lastInvoiceCurrency, r.last_invoice_currency) || 'USD';
  return { amount, currency, paidAt };
}
